import { useTranslation } from 'react-i18next'
import { Banknote, TrendingDown, ShoppingBag } from 'lucide-react'
import { useDashboardStore } from '@/state/useDashboardStore'
import { useSettingsStore } from '@/state/useSettingsStore'
import { KpiCard } from '@/components/kpi/KpiCard'
import { NotReady } from '@/components/kpi/NotReady'
import { AnimatedNumber } from '@/components/kpi/AnimatedNumber'
import { MetricValue } from '@/components/kpi/MetricValue'
import { Delta } from '@/components/kpi/Delta'
import { Sparkline } from '@/components/kpi/Sparkline'
import { formatCzkShort, formatNumber } from '@/lib/format'
import { RevenueMonthlyBars } from '@/components/charts/RevenueMonthlyBars'
import { ChurnBar } from '@/components/charts/ChurnBar'
import { ServiceAdoption } from '@/components/services/ServiceAdoption'

export function SalesFinanceSlide() {
  const { t, i18n } = useTranslation()
  const snapshot = useDashboardStore((s) => s.snapshot)
  const locale = useSettingsStore((s) => s.locale)
  if (!snapshot) return null

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-hidden">
      {}
      <div className="grid grid-cols-5 gap-4">
        <KpiCard
          label={t('kpi.mrr')}
          icon={<Banknote className="size-4" />}
          value={
            <MetricValue
              value={snapshot.revenue.mrrCzk}
              format={(n) => formatCzkShort(n, locale)}
              duration={1.2}
            />
          }
          caption={
            snapshot.revenue.mrrCzk != null
              ? `${t('common.vs_target')}: ${formatCzkShort(snapshot.revenue.targetCzk, locale)}`
              : locale === 'cs'
                ? 'Data zatím nedostupná'
                : 'Data not available yet'
          }
          footer={
            <>
              {snapshot.revenue.mrrDeltaPct != null && <Delta value={snapshot.revenue.mrrDeltaPct} />}
              {/* Průběh příjmu zatím nepočítáme — v řadě je denní netto
                  cashflow, ne příjem ze Stripe, takže by tvarem lhal.
                  Rozostřeno stejně jako ostatní nehotové dlaždice. */}
              <div
                aria-hidden
                className="pointer-events-none h-9 w-24 select-none opacity-40 blur-[6px]"
              >
                <Sparkline values={snapshot.history.mrr30d.map((p) => p.v)} tone="good" />
              </div>
            </>
          }
          tone="brand"
        />
        <KpiCard
          label={locale === 'cs' ? 'Utichlí klienti' : 'Clients gone quiet'}
          icon={<TrendingDown className="size-4" />}
          value={
            <AnimatedNumber
              value={snapshot.clients.wentQuiet7d}
              format={(n) => formatNumber(n, locale)}
            />
          }
          caption={
            locale === 'cs'
              ? 'dřív aktivní, 7 dní bez události'
              : 'previously active, 7 days silent'
          }
          tone={snapshot.clients.wentQuiet7d > 0 ? 'warn' : 'good'}
        />
        <KpiCard
          label={locale === 'cs' ? 'Náklady měsíčně' : 'Monthly costs'}
          icon={<Banknote className="size-4" />}
          value={
            <MetricValue
              value={snapshot.costs.fixedMonthlyCzk}
              format={(n) => formatCzkShort(n, locale)}
            />
          }
          caption={locale === 'cs' ? 'výdaje z banky' : 'expenses from the bank'}
          tone="neutral"
        />

        {/* Dvojnásobná šířka. Drží stejný rytmus jako sousední karty —
            popisek, velké číslo, caption — jen místo sparkline je pruh
            rozdělený podle poboček. */}
        <div className="relative isolate col-span-2 flex h-full flex-col gap-3 overflow-hidden rounded-3xl border border-border bg-surface-1/80 p-5">
          <div className="flex items-start justify-between gap-3">
            <span className="text-xs uppercase tracking-[0.14em] text-ink-subtle">
              {locale === 'cs' ? 'Objednávky tento měsíc' : 'Orders this month'}
            </span>
            <ShoppingBag className="size-4 shrink-0 text-ink-subtle" />
          </div>

          <div className="font-display text-5xl font-semibold leading-none text-ink">
            {formatNumber(snapshot.ordersMonth.total, locale)}
          </div>

          <div className="text-xs text-ink-subtle">
            {locale === 'cs'
              ? `${snapshot.ordersMonth.byVenue.length} poboček`
              : `${snapshot.ordersMonth.byVenue.length} venues`}
          </div>

          <div className="mt-auto pt-3">
            <div className="flex h-1.5 gap-0.5 overflow-hidden rounded-full">
              {snapshot.ordersMonth.byVenue.slice(0, 4).map((v, i) => (
                <span
                  key={v.name}
                  title={`${v.name}: ${v.orders}`}
                  style={{
                    width: `${(v.orders / Math.max(1, snapshot.ordersMonth.total)) * 100}%`,
                    background: 'var(--brand-primary)',
                    opacity: 1 - i * 0.2,
                  }}
                />
              ))}
            </div>
            <div className="mt-2 flex items-baseline justify-between gap-2 text-[11px] text-ink-subtle">
              <span className="truncate">
                {snapshot.ordersMonth.byVenue[0]?.name ?? '—'}
              </span>
              <span className="shrink-0 tabular-nums text-ink">
                {snapshot.ordersMonth.byVenue[0]?.orders ?? 0}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="grid shrink-0 grid-cols-12 gap-4">
        <section className="min-h-0 col-span-12 flex flex-col rounded-3xl border border-border bg-surface-1/70 p-5">
          <h3 className="mb-1 text-xs uppercase tracking-[0.18em] text-ink-subtle">
            {t('kpi.mrr')} · {locale === 'cs' ? 'po měsících' : 'by month'}
          </h3>
          <RevenueMonthlyBars data={snapshot.history.revenueMonthly} height={190} />
        </section>
      </div>

      {}
      <div className="grid min-h-0 flex-1 grid-cols-12 gap-4">
        <section className="min-h-0 col-span-5 flex flex-col rounded-3xl border border-border bg-surface-1/70 p-5">
          <h3 className="mb-3 text-xs uppercase tracking-[0.18em] text-ink-subtle">
            {i18n.language === 'cs' ? 'Nasazení služeb' : 'Service adoption'}
          </h3>
          <div className="flex-1 overflow-auto">
            <ServiceAdoption rows={snapshot.serviceAdoption} totalVenues={snapshot.clients.total} />
          </div>
        </section>
        <section className="min-h-0 col-span-7 flex flex-col rounded-3xl border border-border bg-surface-1/70 p-5">
          <NotReady className="flex min-h-0 flex-1 flex-col">
            <h3 className="mb-3 shrink-0 text-xs uppercase tracking-[0.18em] text-ink-subtle">
              {locale === 'cs' ? 'Synchronizace za 30 dní' : 'Synchronisation, last 30 days'}
            </h3>
          {(() => {
            const sr = snapshot.syncRatio
            const total = sr.success + sr.error + sr.pending
            if (total === 0) {
              return <div className="grid flex-1 place-items-center text-sm text-ink-subtle">—</div>
            }
            const rows: Array<{ label: string; value: number; colorVar: string }> = [
              {
                label: locale === 'cs' ? 'Úspěšně' : 'Succeeded',
                value: sr.success,
                colorVar: 'signal-good',
              },
              {
                label: locale === 'cs' ? 'Chyba' : 'Failed',
                value: sr.error,
                colorVar: 'signal-bad',
              },
              {
                // Ani úspěch, ani chyba — předáno k asynchronnímu zpracování.
                label: locale === 'cs' ? 'Čeká na zpracování' : 'Queued',
                value: sr.pending,
                colorVar: 'signal-warn',
              },
            ]
            return (
              <ul className="flex min-h-0 flex-1 flex-col justify-center gap-3">
                {rows.map((r) => (
                  <li key={r.label} className="flex items-center gap-3">
                    <span className="w-40 shrink-0 text-sm text-ink">{r.label}</span>
                    <span className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-surface-3">
                      <span
                        className="block h-full rounded-full"
                        style={{
                          width: `${(r.value / total) * 100}%`,
                          background: `var(--${r.colorVar})`,
                        }}
                      />
                    </span>
                    <span className="w-16 shrink-0 text-right text-sm tabular-nums text-ink">
                      {formatNumber(r.value, locale)}
                    </span>
                    <span className="w-12 shrink-0 text-right text-xs tabular-nums text-ink-subtle">
                      {Math.round((r.value / total) * 100)} %
                    </span>
                  </li>
                ))}
              </ul>
            )
          })()}
          </NotReady>
        </section>
        <section className="min-h-0 col-span-12 row-start-2 hidden">
          <ChurnBar data={snapshot.history.churn6m} />
        </section>
      </div>
    </div>
  )
}
